import axios from 'axios';
import { createStandaloneToast } from '@chakra-ui/react';
import { showSubscriptionModal } from '../components/SimpleSubscriptionModal';

// ⚠️ O PADRÃO NÃO PODE TERMINAR EM /api. Todas as chamadas deste app já escrevem o
// caminho completo ('api/auth/login'), então uma base com /api produz /api/api/... e
// TUDO responde 404. Era o valor do fallback, e ele entrava em cena sempre que a
// variável de ambiente não chegava ao build — silenciosamente, porque o bundle sai
// sem erro e só quebra no navegador do usuário.
//
// A normalização abaixo torna o erro impossível de reintroduzir por configuração: mesmo
// que alguém ponha /api na variável, ele é removido aqui.
const apiBruta = import.meta.env.VITE_API_URL
  || import.meta.env.VITE_OPENAI_API_URL
  || 'https://api.piloto.life';
const apiURL = String(apiBruta).replace(/\/+$/, '').replace(/\/api$/, '');

// Log the API URL being used
console.log('[API] Using API URL:', apiURL);

// Create axios instance with the exact URL from environment variables
export const api = axios.create({
  baseURL: apiURL,
  // Add a reasonable timeout
  timeout: 30000,
});

// Interceptor para adicionar token em todas as requisições
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('@Piloto:token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
  return config;
});
// Estendendo a interface Window para incluir nossa função global
declare global {
  interface Window {
    openSubscriptionModal?: () => void;
  }
}

// Criando uma instância standalone do toast para uso fora de componentes React
const { toast } = createStandaloneToast();

// Interceptor para tratar erros de autenticação
api.interceptors.response.use(
  response => {
    console.log(`[API Response] ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`);
    return response;
  },
  (error) => {
    console.error(
      `[API Error] ${error.response?.status || "Network Error"} ${
        error.config?.method?.toUpperCase() || ""
      } ${error.config?.url || ""}`,
      error.message
    );

    // Verificar se é o erro específico de assinatura expirada
    // Código de erro 'SUBSCRIPTION_REQUIRED' indica que a assinatura expirou ou está pendente
    if (error.response?.data?.code === "SUBSCRIPTION_REQUIRED") {
      console.log('[API Error] Assinatura expirada ou pendente detectada');
      
      // Exibe um toast para notificação imediata
      toast({
        title: "Assinatura expirada",
        description: "Sua assinatura expirou ou está pendente. Por favor, realize o pagamento para renovar sua assinatura e continuar utilizando a plataforma.",
        status: "error",
        duration: 9000,
        isClosable: true,
        position: "top",
      });
      
      // Tenta usar a função exposta no componente SimpleSubscriptionModal
      showSubscriptionModal();
      
      // Se não funcionar, tenta usar a função global (fallback para compatibilidade)
      if (typeof window.openSubscriptionModal === 'function') {
        window.openSubscriptionModal();
      }
    }
    // Tratamento específico para erros de autenticação
    else if (error.response?.status === 401 || error.response?.status === 403) {
      // ⚠️ NEM TODO 401 É SESSÃO EXPIRADA.
      //
      // Os endpoints de entrada respondem 401 quando a CREDENCIAL está errada, e isso
      // é a resposta normal deles — não um sinal de sessão morta. Tratar igual fazia
      // errar a senha recarregar a página inteira: o formulário remontava, o estado
      // sumia junto, e as opções de recuperação que aparecem depois da falha
      // desapareciam antes de alguém conseguir clicar. Medido em 29/09/2026 — o
      // sintoma era "dá erro, a tela volta pro login e não chega e-mail".
      //
      // Aqui NÃO desloga: quem está tentando entrar já não tem sessão, e a tela de
      // login sabe mostrar o próprio erro.
      const url = String(error.config?.url ?? '');
      const ehTentativaDeEntrar = /\/auth\/(login|activate|link|reportar)$/.test(url);
      if (!ehTentativaDeEntrar) {
        localStorage.removeItem('@Piloto:token');
        localStorage.removeItem('@Piloto:user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);
